import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as projectService from './services/projectService';
import type { ProjectSliceState, Project } from "./types/redux";

interface UpsertProjectPayload {
  project: Project;
  prepend?: boolean;
}

const upsertProjectInList = (projects: Project[], nextProject?: Project, prepend = false): void => {
  if (!nextProject || !nextProject.id) {
    return;
  }

  const targetIndex = projects.findIndex((existing) => existing.id === nextProject.id);

  if (targetIndex >= 0) {
    projects[targetIndex] = {
      ...projects[targetIndex],
      ...nextProject,
    };
    return;
  }

  if (prepend) {
    projects.unshift({ ...nextProject });
  } else {
    projects.push({ ...nextProject });
  }
};

// Async thunks for project operations
// Note: Username no longer required - derived from bearer token by APIM
export const createProject = createAsyncThunk<Project, { name: string }>(
  'project/create',
  async ({ name }, { rejectWithValue }) => {
    try {
      const project = await projectService.createProject(name);
      return project;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchProject = createAsyncThunk<Project, string>(
  'project/fetch',
  async (projectId, { rejectWithValue }) => {
    try {
      const project = await projectService.getProject(projectId);
      return project;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

export const getOrCreateUserProject = createAsyncThunk<Project, { conversationData: unknown; type?: string | null }>(
  'project/getOrCreate',
  async ({ conversationData, type = null }, { rejectWithValue }) => {
    try {
      const project = await projectService.getOrCreateUserProject(conversationData, type);
      return project;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

export const renameProject = createAsyncThunk<Project, { projectId: string; name: string }>(
  'project/rename',
  async ({ projectId, name }, { rejectWithValue }) => {
    try {
      const project = await projectService.updateProject(projectId, { name });
      return project;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProject = createAsyncThunk<string, { projectId: string }>(
  'project/delete',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      await projectService.deleteProject(projectId);
      return projectId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchUserProjects = createAsyncThunk<Project[], void>(
  'project/fetchUserProjects',
  async (_, { rejectWithValue }) => {
    try {
      const projects = await projectService.getUserProjects();
      return projects;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState: ProjectSliceState = {
  // Current active project
  currentProject: null,
  currentProjectId: null,
  projectId: null,

  // All projects
  projects: [],

  // User's projects list
  userProjects: [],
  userProjectsLoading: false,
  userProjectsError: null,

  // Loading states
  loading: false,
  isLoading: false,
  createLoading: false,

  // Error states
  error: null,
  createError: null,

  // Metadata
  initialized: false
};

const projectSlice = createSlice({
  name: "projectSlice",
  initialState,
  reducers: {
    // Legacy actions for backward compatibility
    setProjectId: (state, action: PayloadAction<string>) => {
      const nextProjectId = action.payload;
      const previousProjectId = state.currentProject?.id || null;

      // Check if we're switching to an existing project in the list
      // If so, we should NOT upsert from currentProject (which has stale data)
      const existingProjectInList = state.userProjects.find(
        (project) => project.id === nextProjectId
      );

      // Update the current project ID
      state.currentProjectId = nextProjectId;

      // If clicking an existing project, load its data into currentProject
      // instead of mutating the old currentProject
      if (existingProjectInList) {
        state.currentProject = { ...existingProjectInList };
        return; // Don't do any upsert logic - project already exists
      }

      // Below logic only applies when NOT clicking an existing project
      // (e.g., when a temp project gets a real ID from bootstrap)
      if (state.currentProject) {
        state.currentProject.id = nextProjectId;
        state.currentProject.isTemporary = false;
      }

      // Only upsert when we have a temp project being upgraded to a real ID
      if (previousProjectId && previousProjectId !== nextProjectId && previousProjectId.startsWith('temp-')) {
        const existingIndex = state.userProjects.findIndex(
          (project) => project.id === previousProjectId
        );

        if (existingIndex >= 0) {
          // Replace the temp entry with the real project ID
          state.userProjects[existingIndex] = {
            ...state.userProjects[existingIndex],
            ...state.currentProject,
            id: nextProjectId,
            isTemporary: false,
          };
        } else if (state.currentProject) {
          // Temp entry not found, add the new project
          upsertProjectInList(
            state.userProjects,
            {
              ...state.currentProject,
              id: nextProjectId,
              isTemporary: false,
            },
            true
          );
        }
      } else if (state.currentProject && !state.userProjects.some(p => p.id === nextProjectId)) {
        // Only upsert if project doesn't already exist
        upsertProjectInList(
          state.userProjects,
          {
            ...state.currentProject,
            id: nextProjectId,
            isTemporary: false,
          },
          true
        );
      }
    },
    clearProjectId: (state) => {
      state.currentProjectId = null;
      state.currentProject = null;
    },

    // New project management actions
    setCurrentProject: (state, action: PayloadAction<Project | null>) => {
      state.currentProject = action.payload;
      state.currentProjectId = action.payload?.id || null;
    },

    clearCurrentProject: (state) => {
      state.currentProject = null;
      state.currentProjectId = null;
    },

    clearError: (state) => {
      state.error = null;
      state.createError = null;
    },

    markInitialized: (state, action: PayloadAction<boolean | undefined>) => {
      const value = action?.payload;
      state.initialized = typeof value === 'boolean' ? value : true;
    },

    updateProjectMetadata: (state, action: PayloadAction<{ name?: string; summary?: string }>) => {
      const { name, summary } = action.payload || {};

      if (state.currentProject) {
        // Update existing project object
        if (name !== undefined) state.currentProject.name = name;
        if (summary !== undefined) state.currentProject.summary = summary;
      } else {
        // Create minimal project object if it doesn't exist
        state.currentProject = {
          id: state.currentProjectId,
          name: name || null,
          summary: summary || null
        };
      }

      const activeProjectId = state.currentProject?.id || state.currentProjectId;
      if (activeProjectId) {
        const index = state.userProjects.findIndex((project) => project.id === activeProjectId);
        if (index >= 0) {
          if (name !== undefined) state.userProjects[index].name = name;
          if (summary !== undefined) state.userProjects[index].summary = summary;
        }
      }
    },

    resetProjectState: (state) => {
      Object.assign(state, initialState);
    },

    upsertUserProject: (state, action: PayloadAction<UpsertProjectPayload>) => {
      const { project, prepend = false } = action.payload || {};
      upsertProjectInList(state.userProjects, project, prepend);
    }
  },
  extraReducers: (builder) => {
    // Create project
    builder
      .addCase(createProject.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.createLoading = false;
        state.currentProject = action.payload;
        state.currentProjectId = action.payload.id;
        state.projectId = action.payload.id;
        state.initialized = true;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = typeof action.payload === 'string' ? action.payload : null;
      })

    // Fetch project
    builder
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        const requestedId = action.meta?.arg;
        if (requestedId &&
            requestedId !== state.currentProjectId &&
            requestedId !== state.currentProject?.id) {
          state.loading = false;
          state.isLoading = false;
          return;
        }

        state.loading = false;
        state.isLoading = false;
        state.currentProject = action.payload;
        state.currentProjectId = action.payload.id;
        state.projectId = action.payload.id;
        state.initialized = true; // Mark project as initialized when fetched
      })
      .addCase(fetchProject.rejected, (state, action) => {
        const requestedId = action.meta?.arg;
        if (requestedId &&
            requestedId !== state.currentProjectId &&
            requestedId !== state.currentProject?.id) {
          state.loading = false;
          state.isLoading = false;
          return;
        }

        state.loading = false;
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : null;
      })

    // Get or create user project
    builder
      .addCase(getOrCreateUserProject.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrCreateUserProject.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.currentProject = action.payload;
        state.currentProjectId = action.payload.id;
        state.projectId = action.payload.id;
        state.initialized = true;
      })
      .addCase(getOrCreateUserProject.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : null;
      })

    // Rename project
    builder
      .addCase(renameProject.pending, (state) => {
        state.error = null;
      })
      .addCase(renameProject.fulfilled, (state, action) => {
        const updated = action.payload;
        // Update currentProject if it matches, merging all updated fields
        if (state.currentProject && state.currentProject.id === updated.id) {
          state.currentProject = {
            ...state.currentProject,
            ...updated,
          };
        }
        // Upsert the updated project into userProjects to avoid stale fields
        upsertProjectInList(state.userProjects, updated);
      })
      .addCase(renameProject.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : null;
      })

    // Delete project
    builder
      .addCase(deleteProject.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        const deletedId = action.payload;
        // Remove from userProjects list
        state.userProjects = state.userProjects.filter((p) => p.id !== deletedId);
        // If the deleted project is the current one, clear it
        if (state.currentProject?.id === deletedId || state.currentProjectId === deletedId) {
          state.currentProject = null;
          state.currentProjectId = null;
          state.projectId = null;
          state.initialized = false;
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : null;
      })

    // Fetch user projects
    builder
      .addCase(fetchUserProjects.pending, (state) => {
        state.userProjectsLoading = true;
        state.userProjectsError = null;
      })
      .addCase(fetchUserProjects.fulfilled, (state, action) => {
        state.userProjectsLoading = false;
        state.userProjects = action.payload;
      })
      .addCase(fetchUserProjects.rejected, (state, action) => {
        state.userProjectsLoading = false;
        state.userProjectsError = typeof action.payload === 'string' ? action.payload : null;
      });
  }
});

export const {
  setProjectId,
  clearProjectId,
  setCurrentProject,
  clearCurrentProject,
  clearError,
  markInitialized,
  updateProjectMetadata,
  resetProjectState,
  upsertUserProject
} = projectSlice.actions;

// Selectors
export const selectCurrentProject = (state) => state.projectSlice?.currentProject;
export const selectCurrentProjectId = (state) => state.projectSlice?.currentProjectId;
export const selectProjectLoading = (state) => state.projectSlice?.loading || false;
export const selectCreateProjectLoading = (state) => state.projectSlice?.createLoading || false;
export const selectProjectError = (state) => state.projectSlice?.error;
export const selectCreateProjectError = (state) => state.projectSlice?.createError;
export const selectProjectInitialized = (state) => state.projectSlice?.initialized || false;
export const selectUserProjects = (state) => state.projectSlice?.userProjects || [];
export const selectUserProjectsLoading = (state) => state.projectSlice?.userProjectsLoading || false;
export const selectUserProjectsError = (state) => state.projectSlice?.userProjectsError;

// Combined selector for project + chat bootstrap readiness
export const selectProjectBootstrapComplete = (state) => {
  const projectInitialized = state.projectSlice?.initialized || false;
  const chatBootstrapInitialized = state.chatSlice?.bootstrapInitialized || false;
  return projectInitialized && chatBootstrapInitialized;
};

// Selector for conversation ID from current project
export const selectConversationIdForProject = (state) => {
  return state.projectSlice?.currentProject?.conversationId || null;
};

// Selector for current project type (use case)
export const selectCurrentProjectType = (state) => {
  return state.projectSlice?.currentProject?.type || null;
};

export default projectSlice.reducer;
